import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  Button,
  Space,
  notification,
  Skeleton,
  Tour,
  TourProps,
  InputRef,
} from 'antd';
import { ShopOutlined, BookOutlined, MenuOutlined, RocketOutlined, InfoCircleOutlined } from '@ant-design/icons';
import CreatePageVersionForm from '../components/pageversion/createpageversionform';
import { useAuth } from '../auth/authcontext';
import { getStoresCount } from '../services/storeserivce';
import { getPageVersionCount, createPageVersion } from '../services/pageversionservice';
import { getMenuCount } from '../services/menuservice';
import { getEnvironment } from '../services/homeservice';

const { Title, Paragraph } = Typography;

interface PageVersionValues {
  name: string;
}

interface Metrics {
  activeStores: number;
  activePageVersions: number;
  menuItemsOnOffer: number;
}

const Home = () => {
  const navigate = useNavigate();
  const [isCreateDrawerVisible, setIsCreateDrawerVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [metrics, setMetrics] = useState<Metrics>({
    activeStores: 0,
    activePageVersions: 0,
    menuItemsOnOffer: 0,
  });
  const startRef = useRef<HTMLButtonElement>(null);
  const nameInputRef = useRef<InputRef | null>(null);
  const createButtonRef = useRef<HTMLDivElement | null>(null);
  const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
  const { userId } = useAuth();
  const userGuid: string = userId ?? "";
  const [environment, setEnvironment] = useState<string>("");

  const tourSteps: TourProps['steps'] = [
    {
      title: 'Start Designing',
      description: 'Click here to begin creating a new kiosk design.',
      target: () => startRef.current!,
    },
    {
      title: 'Enter Name',
      description: 'Type the name of your new kiosk version.',
      target: () => nameInputRef.current?.input!,
      placement: 'bottom',
    },
    {
      title: 'Create',
      description: 'Click here to create the new version.',
      target: () => createButtonRef.current!,
      placement: 'bottom',
    },
  ];


  useEffect(() => {
    getEnvironment()
      .then(res => setEnvironment(res.environment))
      .catch(err => console.error("Failed to fetch environment", err));
  }, []);

  const fetchMetrics = useCallback(async () => {
    try {
      const [storesRes, versionsRes, menuRes] = await Promise.all([
        getStoresCount(),
        getPageVersionCount(),
        getMenuCount(),
      ]);

      setMetrics({
        activeStores: storesRes.count,
        activePageVersions: versionsRes.count,
        menuItemsOnOffer: menuRes.count,
      });
    } catch (error) {
      console.error('Error fetching metrics:', error);
      notification.error({
        message: 'Metrics Load Failed',
        description: 'Unable to load dashboard metrics. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleStartDesigning = () => {
    setIsCreateDrawerVisible(true);
  };

  const handleCreateNewVersion = async (values: PageVersionValues): Promise<void> => {
    setIsCreateDrawerVisible(false);

    try {
      const response = await createPageVersion(values.name, userGuid);
      const newPageVersionId = response.id;

      notification.success({
        message: 'Success',
        description: `"${values.name}" PageVersion Created Successfully`,
      });

      navigate(`/kioskpageversion/${newPageVersionId}`);
    } catch (error: any) {
      console.error('Error creating page version:', error);

      let errorMessage = 'Error';
      let errorDescription = 'An unexpected error occurred. Please try again.';

      if (error.response) {
        const { data, status } = error.response as {
          data: { message?: string };
          status: number;
        };

        if (status === 409 && data?.message) {
          errorMessage = 'Creation Conflict';
          errorDescription = data.message;
        } else {
          errorMessage = `Server Error (${status})`;
          errorDescription = data?.message ?? `Server responded with status ${status}.`;
        }
      } else if (error.request) {
        errorMessage = 'Network Error';
        errorDescription = 'No response from server. Please check your internet connection.';
      }

      notification.error({
        message: errorMessage,
        description: errorDescription,
      });
    }
  };

  return (
    <>
      <Tour
        open={isTourOpen}
        onClose={() => {
          setIsCreateDrawerVisible(false);
          setIsTourOpen(false);
        }}
        steps={tourSteps}
      />

      <div style={{ padding: '24px' }}>

        {(environment === "DEV") && (
          <div
            style={{
              color: '#cf1322',
              fontWeight: 'bold',
              textAlign: 'left',
              fontSize: '16px',
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#fff1f0',
              border: '1px solid #ffccc7',
            }}
          >
            THIS IS THE DEV/TEST ENVIRONMENT
          </div>
        )}

        <Title level={2}>Welcome to your Made to Order Operations hub!</Title>
        <Paragraph>
          The all-in-one platform for kiosk operations. Easily design and publish menus to stores,
          and take full control of your stores, menu items, recipes, and addons.
        </Paragraph>

        <Title level={4}>Key Metrics</Title>
        <div className="site-statistic-demo-card">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={8} lg={8}>
              {loading ? (
                <Skeleton active />
              ) : (
                <Card hoverable style={{ backgroundColor: '#f0f5ff' }}>
                  <Statistic
                    title="Stores"
                    value={metrics.activeStores}
                    valueStyle={{ color: '#1677ff' }}
                    prefix={<ShopOutlined />}
                  />
                </Card>
              )}
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              {loading ? (
                <Skeleton active />
              ) : (
                <Card hoverable style={{ backgroundColor: '#f0f5ff' }}>
                  <Statistic
                    title="Active Kiosk Versions"
                    value={metrics.activePageVersions}
                    valueStyle={{ color: '#1677ff' }}
                    prefix={<BookOutlined />}
                  />
                </Card>
              )}
            </Col>
            <Col xs={24} sm={24} md={8} lg={8}>
              {loading ? (
                <Skeleton active />
              ) : (
                <Card hoverable style={{ backgroundColor: '#f0f5ff' }}>
                  <Statistic
                    title="Menu Items on Offer"
                    value={metrics.menuItemsOnOffer}
                    valueStyle={{ color: '#1677ff' }}
                    prefix={<MenuOutlined />}
                  />
                </Card>
              )}
            </Col>
          </Row>
        </div>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Card
            hoverable
            title="Start a New Kiosk Design"
            style={{ backgroundColor: '#e6f4ff', width: '100%', maxWidth: '400px', margin: '0 auto' }}
          >
            <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
              <Paragraph>
                Ready to create a new digital menu? Click the button below to launch the Kiosk Designer.
              </Paragraph>
              <Button
                ref={startRef}
                type="primary"
                icon={<RocketOutlined />}
                onClick={handleStartDesigning}
                size="large"
              >
                Start Designing
              </Button>
              <Button
                type="dashed"
                icon={<InfoCircleOutlined />}
                onClick={() => {
                  setIsCreateDrawerVisible(true);
                  setIsTourOpen(true);
                }}
              >
                Begin Tour
              </Button>
            </Space>
          </Card>
        </div>
      </div >

      <CreatePageVersionForm
        open={isCreateDrawerVisible}
        onClose={() => setIsCreateDrawerVisible(false)}
        onCreate={handleCreateNewVersion}
        nameInputRef={nameInputRef}
        createButtonRef={createButtonRef}
        isTourOpen={isTourOpen}
      />
    </>
  );
};

export default Home;